import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Paper,
  LinearProgress,
  alpha,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Avatar,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Reply as ReplyIcon,
  Info as InfoIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Gavel as GavelIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  decryptText,
  decryptAppId,
} from "../../../utils/encryption";
import s3Utils from '../../../utils/s3Utils';
import PageTitle from '../../../components/PageTitle';

// Modern color palette
const colors = {
  primary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
  },
  secondary: {
    main: "#ec4899",
    light: "#f472b6",
    dark: "#db2777",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

const LaboratoryNCReply = () => {
  const location = useLocation();

  // Function to get parameters from URL
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        return decryptText(encryptedOrgId);
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        return decryptAppId(encryptedAppId);
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        return decryptText(encryptedAssessmentId);
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        return decryptText(encryptedAssessorId);
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  // State management
  const [loading, setLoading] = useState(true);
  const [orgId] = useState(getOrgIdFromQuery());
  const [appId] = useState(getAppIdFromQuery());
  const [assessmentId] = useState(getAssessmentIdFromQuery());
  const [assessorId] = useState(getAssessorIdFromQuery());
  const [nonConformities, setNonConformities] = useState([]);
  const [loadingNC, setLoadingNC] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  // Reply functionality state
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedNcForReply, setSelectedNcForReply] = useState(null);
  const [replyRecipient, setReplyRecipient] = useState("DO"); // Default to DO since lab replies to DO
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyAction, setReplyAction] = useState(""); // "reply"
  const [sendingReply, setSendingReply] = useState(false);
  const [correctiveAction, setCorrectiveAction] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [correctiveActionFiles, setCorrectiveActionFiles] = useState([]);
  const [rootCauseFiles, setRootCauseFiles] = useState([]);

  // Send to DO functionality state
  const [sendingToDO, setSendingToDO] = useState(false);

  // Show snackbar message
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch Non-Conformities data
  const fetchNonConformities = async () => {
    if (!assessmentId || !appId) {
      console.warn("Missing assessmentId or appId for NC fetch");
      setLoading(false);
      return;
    }

    setLoadingNC(true);
    try {
      console.log("🔍 Fetching NC data with:", { assessmentId, appId, assessorId });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/nc-main/GetNCMainall`,
        {
          params: {
            assessmentId: assessmentId,
            appId: appId,
          },
        }
      );

      console.log("📊 NC Response:", response.data);

      if (response.data.isstatus) {
        const ncData = response.data.nc || [];
        
        // Format NC data for better handling
        const formattedNC = ncData.map((item) => ({
          id: item.id,
          clause: item.clause || "N/A",
          description: item.description || "",
          nonconformity: item.nonconformity || "",
          evidence: item.evidence || "",
          requirement: item.requirement || "",
          document: item.document || "",
          ncid: item.ncid,
          assessorid: item.assessorid || "", // Add assessorid for filtering
          cabReply: item.cabReply || "",
          cabReplyDate: item.cabReplyDate || null,
          isLabAccept: item.isLabAccept,
          fileupload: item.fileupload || "",
          assessorRemarks: item.assessorRemarks || "",
          assessorRemarksBy: item.assessorRemarksBy || "",
          assessorRemarksDate: item.assessorRemarksDate || null,
          stage: item.stage !== undefined ? item.stage : null,
          isclsosed: item.isclsosed || "False",
          ncreply: item.ncreply || [],
          doReply: item.doReply || "0",
          doIsClosed: item.doIsClosed || "0",
        }));

        // Filter NCs by assessorId if provided in URL query
        let filteredByAssessor = formattedNC;
        if (assessorId && assessorId.trim() !== '') {
          filteredByAssessor = formattedNC.filter((nc) => {
            const ncAssessorId = nc.assessorid?.toString().trim();
            const urlAssessorId = assessorId?.toString().trim();
            console.log(`🔍 Filtering NC ${nc.id}: NC assessorId="${ncAssessorId}", URL assessorId="${urlAssessorId}"`);
            return ncAssessorId === urlAssessorId;
          });
          console.log(`📊 Filtered ${filteredByAssessor.length} NCs out of ${formattedNC.length} total NCs for assessorId: ${assessorId}`);
        }

        // Filter NCs to show only those where latest reply's replytoId matches current orgId
        const finalFilteredNC = filteredByAssessor.filter(nc => shouldShowNC(nc));

        setNonConformities(finalFilteredNC);
        console.log("✅ NC data processed:", formattedNC);
        console.log(`🔍 Final filtered NCs (assessorId + latest reply filtering):`, finalFilteredNC);
        
        // Show appropriate message based on filtering results
        if (assessorId && assessorId.trim() !== '' && finalFilteredNC.length === 0 && formattedNC.length > 0) {
          showSnackbar(`No non-conformities found for assessor ID: ${assessorId}`, "info");
        } else if (finalFilteredNC.length === 0) {
          showSnackbar("No non-conformities found for this assessment", "info");
        }
      } else {
        console.warn("❌ NC API returned false status:", response.data.message);
        showSnackbar("No non-conformities found for this assessment", "info");
      }
    } catch (error) {
      console.error("❌ Error fetching NC data:", error);
      showSnackbar("Failed to load non-conformities data", "error");
    } finally {
      setLoadingNC(false);
      setLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    console.log("🚀 LaboratoryNCReply component initialized with params:", {
      orgId,
      appId,
      assessmentId,
      assessorId,
    });

    if (assessorId && assessorId.trim() !== '') {
      console.log(`🎯 Assessor filtering enabled: Will show only NCs for assessorId="${assessorId}"`);
    } else {
      console.log("📋 No assessor filtering: Will show all NCs for this assessment");
    }

    if (appId && assessmentId) {
      fetchNonConformities();
    } else {
      console.warn("⚠️ Missing required parameters:", {
        appId: !!appId,
        assessmentId: !!assessmentId,
      });
      setLoading(false);
      showSnackbar("Missing required parameters for NC data", "error");
    }
  }, [orgId, appId, assessmentId, assessorId]);

  // Handle image lightbox
  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  // Helper function to check if Update Reply button should be shown
  const shouldShowUpdateReplyButton = (nc) => {
    // Only show Update Reply button when doReply is "1"
    return nc.doReply === "1";
  };

  // Helper function to get the latest DO reply
  const getLatestDOReply = (nc) => {
    if (!nc.ncreply || nc.ncreply.length === 0) return null;
    
    // Filter DO replies and sort by date (most recent first)
    const doReplies = nc.ncreply
      .filter(reply => reply.replyby === "DO")
      .sort((a, b) => new Date(b.insertdate) - new Date(a.insertdate));
    
    return doReplies.length > 0 ? doReplies[0] : null;
  };

  // Helper function to get the latest NC reply (from any sender)
  const getLatestNCReply = (nc) => {
    if (!nc.ncreply || nc.ncreply.length === 0) return null;
    console.log("🔍 Getting latest NC reply for NC ID:", nc.id);
    // Sort all replies by date (most recent first)
    const sortedReplies = nc.ncreply
      .sort((a, b) => new Date(b.insertdate) - new Date(a.insertdate));
    
    return sortedReplies.length > 0 ? sortedReplies[0] : null;
  };

  // Helper function to check if NC should be shown (latest reply directed to current lab)
  const shouldShowNC = (nc) => {
    if (!orgId) return true; // Show all if no orgId
    
    // Show NC if doReply is "2" (Lab can update reply)
    if (nc.doReply === "2") return true;
    
    const latestReply = getLatestNCReply(nc);
    if (!latestReply) return true; // Show if no replies
    
    // Show if latest reply's replytoId matches current orgId (as string)
    if (latestReply.replytoId === orgId || latestReply.replytoId === orgId.toString()) {
      return true;
    }
    
    // Show if latest reply is to "Lab" (generic lab reference)
    if (latestReply.replytoId === "Lab") return true;
    
    // Show if there's a Lab reply from this orgId and DO has responded to Lab
    const hasLabReply = nc.ncreply && nc.ncreply.some(reply => 
      reply.replyby === "Lab" && reply.replyById === orgId
    );
    const hasDoReplyToLab = nc.ncreply && nc.ncreply.some(reply => 
      reply.replyby === "DO" && (reply.replyto === "Lab" || reply.replytoId === orgId)
    );
    
    if (hasLabReply && hasDoReplyToLab) return true;
    
    return false;
  };

  // Reply Dialog Handlers
  const handleOpenReplyDialog = (nc, action = "reply") => {
    setSelectedNcForReply(nc);
    setReplyAction(action);
    setReplyRecipient("DO"); // Lab always replies to DO
    setReplyRemarks(nc.cabReply || "");
    setCorrectiveAction("");
    setRootCause("");
    setCorrectiveActionFiles([]);
    setRootCauseFiles([]);
    setReplyDialogOpen(true);
  };

  const handleCloseReplyDialog = () => {
    setReplyDialogOpen(false);
    setSelectedNcForReply(null);
    setReplyRecipient("");
    setReplyRemarks("");
    setReplyAction("");
    setCorrectiveAction("");
    setRootCause("");
    setCorrectiveActionFiles([]);
    setRootCauseFiles([]);
  };

  // Handle Corrective Action file upload
  const handleCorrectiveActionFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("orgId", orgId);
        formData.append("section", "nc-corrective-action");
        formData.append("fieldName", `corrective_${selectedNcForReply?.id}_${Date.now()}`);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/upload/single`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          return {
            fileName: file.name,
            url: response.data.data.url,
          };
        } else {
          throw new Error(response.data.error || "Upload failed");
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      setCorrectiveActionFiles((prev) => [...prev, ...uploadResults]);

      showSnackbar("Corrective action file(s) uploaded!", "success");
    } catch (error) {
      console.error("❌ Error uploading corrective action files:", error);
      showSnackbar("Error uploading corrective action files", "error");
    } finally {
      setUploadingFiles(false);
    }
  };

  // Handle Root Cause file upload
  const handleRootCauseFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("orgId", orgId);
        formData.append("section", "nc-root-cause");
        formData.append("fieldName", `rootcause_${selectedNcForReply?.id}_${Date.now()}`);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/upload/single`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          return {
            fileName: file.name,
            url: response.data.data.url,
          };
        } else {
          throw new Error(response.data.error || "Upload failed");
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      setRootCauseFiles((prev) => [...prev, ...uploadResults]);

      showSnackbar("Root cause file(s) uploaded!", "success");
    } catch (error) {
      console.error("❌ Error uploading root cause files:", error);
      showSnackbar("Error uploading root cause files", "error");
    } finally {
      setUploadingFiles(false);
    }
  };

  // Remove corrective action file
  const removeCorrectiveActionFile = (index) => {
    setCorrectiveActionFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Remove root cause file
  const removeRootCauseFile = (index) => {
    setRootCauseFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Send Reply to DO
  const handleSendReply = async () => {
    if (!replyRemarks.trim() || !selectedNcForReply) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }

    try {
      setSendingReply(true);

      // Prepare file upload URLs
      let correctiveActionFileUrls = "";
      let rootCauseFileUrls = "";

      // Format corrective action files URLs
      if (correctiveActionFiles.length > 0) {
        correctiveActionFileUrls = correctiveActionFiles.map(file => file.url).join("~");
      }

      // Format root cause files URLs
      if (rootCauseFiles.length > 0) {
        rootCauseFileUrls = rootCauseFiles.map(file => file.url).join("~");
      }

      // First, use the direct CAB reply endpoint to update the NC_main table
      const cabReplyPayload = {
        cabReply: replyRemarks.trim(),
        fileupload: correctiveActionFileUrls // Using corrective action files as main fileupload
      };

      const cabReplyUrl = `${process.env.REACT_APP_API_URL}/nc-main/cab-reply/${selectedNcForReply.id}`;
      const cabReplyResponse = await axios.put(cabReplyUrl, cabReplyPayload);

      if (cabReplyResponse.data.success) {
        // Then, add to the nc reply trail for communication history
        const ncReplyPayload = {
          NCId: selectedNcForReply.id,
          ReplyRemarks: replyRemarks.trim(),
          ReplyById: orgId || "Lab",
          CorrectiveAction: correctiveAction.trim(),
          CorrectiveActionupload: correctiveActionFileUrls,
          rootcause: rootCause.trim(),
          rootcauseupload: rootCauseFileUrls,
          Replyby: "Lab", // Lab is sending the reply
          replyto: "DO", // Always to DO
          replytoId: "DO", // DO identifier
        };

        const ncReplyUrl = `${process.env.REACT_APP_API_URL}/nc-main/ncreply`;
        await axios.post(ncReplyUrl, ncReplyPayload);

        showSnackbar("Reply sent to DO successfully", "success");
        handleCloseReplyDialog();
        // Refresh NC data
        fetchNonConformities();
      } else {
        showSnackbar("Failed to send reply", "error");
      }
    } catch (error) {
      console.error("❌ Error sending reply:", error);
      showSnackbar("Error sending reply", "error");
    } finally {
      setSendingReply(false);
    }
  };

  // Send all NCs to DO
  const handleSendToDO = async () => {
    if (nonConformities.length === 0) {
      showSnackbar("No non-conformities to send", "warning");
      return;
    }

    try {
      setSendingToDO(true);
      
      // Update all NCs to set doReply = '2'
      const ncIds = nonConformities.map(nc => nc.id);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/nc-main/sendAllNCsToDO`,
        {
          ncIds: ncIds,
          appId: appId,
          assessmentId: assessmentId,
          orgId: orgId
        }
      );

      if (response.data.success) {
        showSnackbar("All non-conformities sent to DO successfully", "success");
        // Refresh NC data
        fetchNonConformities();
      } else {
        showSnackbar(response.data.message || "Failed to send NCs to DO", "error");
      }
    } catch (error) {
      console.error("❌ Error sending NCs to DO:", error);
      showSnackbar("Error sending non-conformities to DO", "error");
    } finally {
      setSendingToDO(false);
    }
  };

  // NC Reply Trail Display Component
  const NCReplyTrail = ({ nc }) => {
    if (!nc.ncreply || nc.ncreply.length === 0) {
      return null;
    }

    // Sort replies by date (oldest first)
    const sortedReplies = nc.ncreply.sort((a, b) => new Date(a.insertdate) - new Date(b.insertdate));

    return (
      <Accordion sx={{ boxShadow: 'none', border: 'none' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: alpha(colors.primary.main, 0.05),
            '&:hover': {
              bgcolor: alpha(colors.primary.main, 0.1)
            },
            borderRadius: 2,
            mb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="subtitle2" fontWeight={600} color="primary.main">
              NC COMMUNICATION TRAIL ({sortedReplies.length} entries)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ position: 'relative' }}>
            {sortedReplies.map((reply, replyIndex) => (
              <Box 
                key={reply.id || replyIndex}
                sx={{ 
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  position: 'relative'
                }}
              >
                {/* Timeline Dot */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: reply.replyby === 'Lab' || reply.replyby === 'Cab' 
                        ? colors.success.main 
                        : reply.replyby === 'Assessor' 
                          ? colors.primary.main 
                          : colors.secondary.main, // DO
                      border: '2px solid white',
                      boxShadow: 2
                    }}
                  >
                    {reply.replyby === 'Lab' || reply.replyby === 'Cab' ? (
                      <BusinessIcon sx={{ fontSize: 16, color: 'white' }} />
                    ) : reply.replyby === 'Assessor' ? (
                      <PersonIcon sx={{ fontSize: 16, color: 'white' }} />
                    ) : (
                      <GavelIcon sx={{ fontSize: 16, color: 'white' }} />
                    )}
                  </Avatar>
                  {/* Timeline Connector */}
                  {replyIndex < sortedReplies.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: 32,
                        width: 2,
                        height: 40,
                        bgcolor: colors.grey[300],
                        transform: 'translateX(-50%)',
                        zIndex: 0
                      }}
                    />
                  )}
                </Box>

                {/* Timeline Content */}
                <Box sx={{ flex: 1, pt: 0.5 }}>
                  <Card
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: reply.replyby === 'Lab' || reply.replyby === 'Cab'
                        ? alpha(colors.success.main, 0.05)
                        : reply.replyby === 'Assessor'
                          ? alpha(colors.primary.main, 0.05)
                          : alpha(colors.secondary.main, 0.05),
                      border: `1px solid ${
                        reply.replyby === 'Lab' || reply.replyby === 'Cab'
                          ? colors.success.main
                          : reply.replyby === 'Assessor'
                            ? colors.primary.main
                            : colors.secondary.main
                      }`
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Chip
                        label={reply.replyby}
                        size="small"
                        color={
                          reply.replyby === 'Lab' || reply.replyby === 'Cab'
                            ? 'success'
                            : reply.replyby === 'Assessor'
                              ? 'primary'
                              : 'secondary'
                        }
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(reply.insertdate).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                    </Box>
                    
                    {reply.replyRemarks && (
                      <Typography variant="body2" sx={{ mb: 2, color: colors.grey[800] }}>
                        <strong>Reply:</strong> {reply.replyRemarks}
                      </Typography>
                    )}

                    {reply.correctiveAction && (
                      <Typography variant="body2" sx={{ mb: 1, color: colors.grey[700] }}>
                        <strong>Corrective Action:</strong> {reply.correctiveAction}
                      </Typography>
                    )}

                    {reply.rootcause && (
                      <Typography variant="body2" sx={{ color: colors.grey[700] }}>
                        <strong>Root Cause:</strong> {reply.rootcause}
                      </Typography>
                    )}
                  </Card>
                </Box>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Working Space Document Component
  const WorkingSpaceDocument = ({
    doc,
    index,
    isImage,
    isPdf,
    isExcel,
    colors,
    onImageClick,
  }) => {
    // Check if doc is already a full URL or just a filename
    const imageUrl = doc.startsWith('http') ? doc : `https://nabl7t.s3.ap-south-1.amazonaws.com/live/${doc}`;

    // For PDFs and Excel files, show an icon
    if (isPdf || isExcel) {
      return (
        <Box
          sx={{
            width: 80,
            height: 80,
            border: `2px solid ${colors.grey[300]}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: colors.primary.main,
              transform: "scale(1.05)",
              boxShadow: 3,
            },
          }}
          onClick={() => window.open(imageUrl, "_blank")}
        >
          {isPdf ? (
            <PictureAsPdfIcon sx={{ fontSize: 24, color: colors.error.main }} />
          ) : (
            <DescriptionIcon sx={{ fontSize: 24, color: colors.success.main }} />
          )}
          <Typography
            variant="caption"
            sx={{ mt: 0.5, fontSize: "0.6rem", textAlign: "center" }}
          >
            {isPdf ? "PDF" : "Excel"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.40rem" }}
          >
            Click to view
          </Typography>
        </Box>
      );
    }

    // For images, show the actual image
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: colors.primary.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() =>
          onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
        }
      >
        <img
          src={imageUrl}
          alt={`Evidence ${index + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            fallback:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E",
          }}
        />
      </Box>
    );
  };

  // Main render
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          gap: 2,
        }}
      >
        <LinearProgress sx={{ width: "100%", maxWidth: "400px" }} />
        <Typography variant="body2" color="text.secondary">
          Loading Non-Conformities...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(
          colors.primary.main,
          0.02
        )} 0%, ${alpha(colors.secondary.main, 0.02)} 100%)`,
        pt: 2,
        pb: 4,
      }}
    >
      <PageTitle title="NABL | Laboratory - Non-Conformities Reply" />

      {/* Header Information */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
          background: "white",
          border: `1px solid ${colors.grey[200]}`,
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <AssignmentIcon sx={{ color: colors.primary.main }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: colors.grey[900] }}
              >
                Non-Conformities Reply - Laboratory Dashboard
              </Typography>
              <Chip
                label={(assessorId && assessorId.trim() !== '') ? 
                  `${nonConformities.length} Pending Non-Conformities (Filtered by Assessor: ${assessorId})` : 
                  `${nonConformities.length} Pending Non-Conformities`
                }
                color={(assessorId && assessorId.trim() !== '') ? "secondary" : "primary"}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          }
          sx={{
            background: `linear-gradient(135deg, ${alpha(
              colors.primary.main,
              0.05
            )}, ${alpha(colors.secondary.main, 0.05)})`,
          }}
        />
      </Card>

      {/* Non-Conformities List */}
      <Box>
        {loadingNC ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading non-conformities data...
            </Typography>
          </Box>
        ) : nonConformities.length === 0 ? (
          <Card
            sx={{
              textAlign: "center",
              py: 6,
              borderRadius: 3,
              border: `2px dashed ${colors.grey[300]}`,
            }}
          >
            <InfoIcon
              sx={{ fontSize: 48, color: colors.grey[400], mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Non-Conformities Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This assessment has no non-conformities to reply to.
            </Typography>
          </Card>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {nonConformities.map((nc, index) => (
              <Paper
                key={nc.id}
                elevation={2}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  border: `1px solid ${colors.grey[200]}`,
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: `0 8px 25px ${alpha(colors.primary.main, 0.15)}`,
                    borderColor: colors.primary.main,
                  },
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      colors.error.main,
                      0.05
                    )}, ${alpha(colors.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${colors.grey[200]}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: colors.grey[900],
                      }}
                    >
                      {`NC${String(index + 1).padStart(3, "0")}`}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Chip
                        label={`Clause ${nc.clause}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(colors.primary.main, 0.1),
                          color: colors.primary.main,
                          fontWeight: 600,
                        }}
                      />
                      {nc.isLabAccept && (
                        <Chip
                          label={nc.isLabAccept === "1" ? "Accepted" : "Not Accepted"}
                          color={nc.isLabAccept === "1" ? "success" : "error"}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {nc.description && (
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.grey[800],
                        fontWeight: 500,
                        p: 2,
                        mb: 2,
                        backgroundColor: colors.grey[50],
                        borderRadius: 2,
                        borderLeft: `4px solid ${colors.error.main}`,
                      }}
                    >
                      {nc.description}
                    </Typography>
                  )}

                  {nc.nonconformity && (
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[700], mb: 1 }}
                    >
                      <strong>Non-Conformity:</strong> {nc.nonconformity}
                    </Typography>
                  )}

                  {nc.requirement && (
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[700], mb: 1 }}
                    >
                      <strong>Requirement:</strong> {nc.requirement}
                    </Typography>
                  )}
                </Box>

                {/* Evidence Section */}
                {nc.evidence && nc.evidence.trim() && (
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: colors.grey[700],
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <DescriptionIcon fontSize="small" />
                      Supporting Evidence:
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: 1.5,
                        maxWidth: "100%",
                      }}
                    >
                      {nc.evidence
                        .split("~")
                        .filter(Boolean)
                        .map((doc, docIndex) => {
                          const docLower = doc.toLowerCase();
                          const isImage = docLower.match(
                            /\.(jpg|jpeg|png|gif|webp)$/
                          );
                          const isPdf = docLower.endsWith(".pdf");
                          const isExcel = docLower.match(/\.(xlsx|xls)$/);

                          return (
                            <WorkingSpaceDocument
                              key={docIndex}
                              doc={doc}
                              index={docIndex}
                              isImage={isImage}
                              isPdf={isPdf}
                              isExcel={isExcel}
                              colors={colors}
                              onImageClick={
                                isImage ? handleOpenLightbox : null
                              }
                            />
                          );
                        })}
                    </Box>
                  </Box>
                )}

                {/* Current Laboratory Reply Section */}
                {nc.cabReply && (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(colors.success.main, 0.05),
                      borderTop: `1px solid ${colors.grey[200]}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: colors.success.dark,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <ReplyIcon fontSize="small" />
                      Your Current Reply:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.grey[800],
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${colors.grey[200]}`,
                      }}
                    >
                      {nc.cabReply}
                    </Typography>
                    {nc.cabReplyDate && (
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey[600], mt: 1, display: "block" }}
                      >
                        Reply Date: {new Date(nc.cabReplyDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Latest DO Reply Section */}
                {(() => {
                  const latestDOReply = getLatestDOReply(nc);
                  return latestDOReply && (
                    <Box
                      sx={{
                        p: 3,
                        backgroundColor: alpha(colors.secondary.main, 0.05),
                        borderTop: `1px solid ${colors.grey[200]}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: colors.secondary.dark,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <GavelIcon fontSize="small" />
                        Latest DO Reply:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.grey[800],
                          backgroundColor: "white",
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${colors.grey[200]}`,
                          mb: 1,
                        }}
                      >
                        {latestDOReply.replyRemarks}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey[600], display: "block" }}
                      >
                        DO Reply Date: {new Date(latestDOReply.insertdate).toLocaleDateString()} at {new Date(latestDOReply.insertdate).toLocaleTimeString()}
                      </Typography>
                      {latestDOReply.replyto && (
                        <Typography
                          variant="caption"
                          sx={{ color: colors.grey[600], display: "block", mt: 0.5 }}
                        >
                          Reply to: {latestDOReply.replyto}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}

                {/* NC Reply Trail/History */}
                {nc.ncreply && nc.ncreply.length > 0 && (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(colors.grey[50], 0.5),
                      borderTop: `1px solid ${colors.grey[200]}`,
                    }}
                  >
                    <NCReplyTrail nc={nc} />
                  </Box>
                )}

                {/* Laboratory Action Buttons Section */}
                {shouldShowUpdateReplyButton(nc) && (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(colors.grey[100], 0.5),
                      borderTop: `1px solid ${colors.grey[200]}`,
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: colors.grey[600], mr: 2 }}
                    >
                      Laboratory Actions:
                    </Typography>
                    
                    <Tooltip title="Update your reply to this Non-Conformity">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ReplyIcon />}
                        onClick={() => handleOpenReplyDialog(nc, "reply")}
                        sx={{
                          background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                          color: "white",
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          px: 2,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.primary.main})`,
                            transform: "translateY(-1px)",
                            boxShadow: 4,
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        Update Reply
                      </Button>
                    </Tooltip>
                  </Box>
                )}

                {/* NC Status Display */}
                <Box
                  sx={{
                    px: 3,
                    pb: 2,
                    backgroundColor: alpha(colors.grey[100], 0.3),
                  }}
                >
                  <Chip
                    label={
                      nc.isclsosed === "True" || nc.isclsosed === "1" || nc.isclsosed === true
                        ? "NC Closed"
                        : "NC Open"
                    }
                    color={
                      nc.isclsosed === "True" || nc.isclsosed === "1" || nc.isclsosed === true
                        ? "success"
                        : "warning"
                    }
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Info Message with Send to DO Button */}
      {nonConformities.length > 0 && !nonConformities.every(nc => nc.doReply === "2") && (
        <Card
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            border: `1px solid ${colors.primary.main}`,
            backgroundColor: alpha(colors.primary.main, 0.05),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              <InfoIcon sx={{ color: colors.primary.main }} />
              <Typography
                variant="body2"
                sx={{ color: colors.primary.dark, fontWeight: 500 }}
              >
                Reply to non-conformities raised during the assessment. Your responses will be sent to the DO for review.
                Include corrective actions and root cause analysis as appropriate.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSendToDO}
              disabled={sendingToDO}
              startIcon={
                sendingToDO ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                background: `linear-gradient(135deg, ${colors.success.main}, ${colors.primary.main})`,
                color: "white",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                minWidth: 140,
                "&:hover": {
                  background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.success.main})`,
                  transform: "translateY(-1px)",
                  boxShadow: 4,
                },
                transition: "all 0.2s ease",
              }}
            >
              {sendingToDO ? "Sending..." : "Send All to DO"}
            </Button>
          </Box>
        </Card>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Reply Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={handleCloseReplyDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${colors.grey[200]}`,
          backgroundColor: colors.grey[50],
          fontWeight: 600
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReplyIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Reply to Non-Conformity
            </Typography>
            {selectedNcForReply && (
              <Chip
                label={`NC${String(nonConformities.indexOf(selectedNcForReply) + 1).padStart(3, "0")}`}
                color="primary"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedNcForReply && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                NC Details
              </Typography>
              <Box sx={{ bgcolor: colors.grey[50], p: 2, borderRadius: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Clause:</strong> {selectedNcForReply.clause}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {selectedNcForReply.description}
                </Typography>
              </Box>

              {/* Reply Message */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Reply Message *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={replyRemarks}
                  onChange={(e) => setReplyRemarks(e.target.value)}
                  placeholder="Enter your reply to the DO regarding this non-conformity..."
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>

              {/* Corrective Action */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Corrective Action
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={correctiveAction}
                  onChange={(e) => setCorrectiveAction(e.target.value)}
                  placeholder="Describe the corrective action taken or planned..."
                  variant="outlined"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <input
                  type="file"
                  multiple
                  onChange={handleCorrectiveActionFileUpload}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                  style={{ display: 'none' }}
                  id="corrective-action-upload"
                />
                <label htmlFor="corrective-action-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploadingFiles}
                    sx={{
                      borderColor: colors.primary.main,
                      color: colors.primary.main,
                      borderRadius: 2,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: colors.primary.dark,
                        backgroundColor: alpha(colors.primary.main, 0.1),
                      },
                    }}
                  >
                    Upload Corrective Action Files
                  </Button>
                </label>

                {correctiveActionFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {correctiveActionFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.fileName}
                        size="small"
                        onDelete={() => removeCorrectiveActionFile(index)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Root Cause */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Root Cause Analysis
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={rootCause}
                  onChange={(e) => setRootCause(e.target.value)}
                  placeholder="Describe the root cause analysis..."
                  variant="outlined"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <input
                  type="file"
                  multiple
                  onChange={handleRootCauseFileUpload}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                  style={{ display: 'none' }}
                  id="root-cause-upload"
                />
                <label htmlFor="root-cause-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploadingFiles}
                    sx={{
                      borderColor: colors.primary.main,
                      color: colors.primary.main,
                      borderRadius: 2,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: colors.primary.dark,
                        backgroundColor: alpha(colors.primary.main, 0.1),
                      },
                    }}
                  >
                    Upload Root Cause Files
                  </Button>
                </label>

                {rootCauseFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {rootCauseFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.fileName}
                        size="small"
                        onDelete={() => removeRootCauseFile(index)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseReplyDialog}
            variant="outlined"
            sx={{
              borderColor: colors.grey[300],
              color: colors.grey[600],
              "&:hover": {
                borderColor: colors.grey[400],
                backgroundColor: alpha(colors.grey[400], 0.1),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendReply}
            variant="contained"
            disabled={sendingReply || uploadingFiles || !replyRemarks.trim()}
            startIcon={
              sendingReply || uploadingFiles ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            sx={{
              background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
              color: "white",
              minWidth: 120,
              "&:hover": {
                background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.primary.main})`,
              },
            }}
          >
            {sendingReply ? "Sending..." : uploadingFiles ? "Uploading..." : "Send Reply"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Lightbox (if needed) */}
      {lightboxOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={handleCloseLightbox}
        >
          <img
            src={lightboxImage}
            alt="Evidence"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default LaboratoryNCReply;